import { Fragment, StrictMode, useLayoutEffect } from 'react';
import { Metrics } from './metrics';
import { render } from 'react-dom';
import { createRoot } from 'react-dom/client';
// @ts-expect-error untyped loader
import { name as packageName } from 'current-package-loader!';
import { getShouldStartWithCopilotOpen } from './copilot';
import { GridStyles, setupGridClasses } from './grid';
import { QUARTZ_APP_CONTAINER_CLASS } from 'quartz/grid';
import { clearMarks, clearMeasures, getEntriesByName, mark, measure } from './perf';
import { getCopilotLayoutType } from '../framework/copilot';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export function Root({
  children
}) {
  useLayoutEffect(() => {
    mark('quartz.afterRootRender');

    // `performance.mark()` doesn't return a performance `PerformanceMark` in some browsers, so we use `getEntriesByName`
    // See: https://developer.mozilla.org/en-US/docs/Web/API/Performance/mark#browser_compatibility
    getEntriesByName('quartz.appEntry', 'mark').forEach(entry => {
      Metrics.timer('appEntry').update(entry.startTime);
    });
    getEntriesByName('quartz.authFinished', 'mark').forEach(entry => {
      Metrics.timer('authFinished').update(entry.startTime);
    });
    clearMarks('quartz.authFinished');
    getEntriesByName('quartz.prepareAppFinished', 'mark').forEach(entry => {
      Metrics.timer('prepareAppFinished').update(entry.startTime);
    });
    clearMarks('quartz.prepareAppFinished');
    getEntriesByName('quartz.beforeRootRender', 'mark').forEach(entry => {
      Metrics.timer('beforeRootRender').update(entry.startTime);
    });
    measure('quartz.rootRender', 'quartz.beforeRootRender', 'quartz.afterRootRender');
    clearMarks('quartz.afterRootRender');

    // The rough time it takes to get from our app entry to the handoff to the
    // app render
    measure('quartz.startupTime', 'quartz.appEntry', 'quartz.beforeRootRender');
    clearMarks('quartz.appEntry');
    clearMarks('quartz.beforeRootRender');

    // `performance.measure()` doesn't return a performance `PerformanceMeasure` in some browsers, so we use `getEntriesByName`
    // See: https://developer.mozilla.org/en-US/docs/Web/API/Performance/measure#browser_compatibility
    getEntriesByName('quartz.rootRender', 'measure').forEach(entry => {
      Metrics.timer('rootRender').update(entry.duration);
    });
    clearMeasures('quartz.rootRender');

    // `performance.measure()` doesn't return a performance `PerformanceMeasure` in some browsers, so we use `getEntriesByName`
    // See: https://developer.mozilla.org/en-US/docs/Web/API/Performance/measure#browser_compatibility
    getEntriesByName('quartz.startupTime', 'measure').forEach(entry => {
      Metrics.timer('appStartup').update(entry.duration);
    });
    clearMeasures('quartz.startupTime');
  }, []);
  return children;
}
function getLegacyRootLocalStorageOverride() {
  try {
    const value = localStorage.getItem('LEGACY_ROOT_OVERRIDE');
    const parsedValue = value !== null ? JSON.parse(value) : null;
    return typeof parsedValue === 'boolean' ? parsedValue : null;
  } catch (_unused) {
    return null;
  }
}
export const calculateCopilotLayoutType = copilotLayoutType => {
  if (copilotLayoutType) {
    return copilotLayoutType;
  }
  try {
    return getCopilotLayoutType();
  } catch (_unused2) {
    return copilotLayoutType;
  }
};
export function renderRoot(reactElement, {
  copilotLayoutType: copilotLayoutTypeFromProps,
  strictMode = false,
  legacyRoot = true
} = {}) {
  let container;
  const copilotLayoutType = calculateCopilotLayoutType(copilotLayoutTypeFromProps);
  if (copilotLayoutType && copilotLayoutType !== 'panel') {
    container = document.querySelector(`.${QUARTZ_APP_CONTAINER_CLASS}`);
    if (!container) {
      Metrics.counter('quartz.copilotLayout.noContainer').increment();
      container = document.querySelector('.page');
    }
  } else {
    container = document.querySelector('.page');
  }
  if (!container) {
    throw new Error('Invariant: Could not find container element');
  }
  if (copilotLayoutType) {
    setupGridClasses(copilotLayoutType);
  }
  const StrictModeIfEnabled = strictMode ? StrictMode : Fragment;
  const children = /*#__PURE__*/_jsx(Root, {
    children: /*#__PURE__*/_jsxs(StrictModeIfEnabled, {
      children: [copilotLayoutType && /*#__PURE__*/_jsx(GridStyles, {
        shouldSidebarStartOpen: getShouldStartWithCopilotOpen()
      }), reactElement]
    })
  });
  mark('quartz.beforeRootRender');
  const legacyRootOverride = getLegacyRootLocalStorageOverride();
  if (legacyRootOverride !== null) {
    legacyRoot = legacyRootOverride;
  }
  if (!legacyRoot) {
    createRoot(container, {
      identifierPrefix: packageName
    }).render(children);
  } else {
    render(children, container);
  }
}